import { useEffect, useRef } from 'react';
import type {
  MappingParameterRepairKind,
  MappingProjectReadinessResult,
} from '../lib/mappingProjectReadiness';
import { infoMappingFlow, toTraceKey, traceMappingFlow } from '../lib/mappingTrace';

interface MappingReadinessDiagnosticsInput {
  projectKey: string | null;
  normalizedProjectKey: string | null;
  modelVersion: number | null;
  selectedProfileKey: string;
  signature: string;
  readinessResult: MappingProjectReadinessResult;
  referenceCount: number;
  catalogReady: boolean;
  catalogLoading: boolean;
  waitingForSampledCatalog: boolean;
  catalogUnavailable: boolean;
  runtimeAvailable: boolean;
  hostRuntimeAvailable: boolean;
  projectCatalog: {
    isLoaded: boolean;
    matchesProject: boolean;
    error: string | null;
    parameterNames: string[];
  };
  sampledCatalog: {
    isReady: boolean;
    isLoading: boolean;
    names: string[];
  };
  familySatisfaction: {
    applies: boolean;
    isReady: boolean;
    satisfiedCount: number;
  };
  repairKinds: MappingParameterRepairKind[];
  repair: {
    hasAttemptedRepair: boolean;
    isRepairing: boolean;
    repairErrorMessage: string | null;
  };
}

export function useMappingReadinessDiagnostics(input: MappingReadinessDiagnosticsInput): void {
  const traceStateKey = JSON.stringify(input);
  const missingParameterKey = input.readinessResult.missingParameterNames.join('|');
  const blockedParameterKey = input.readinessResult.blockedParameterNames.join('|');
  const repairableParameterKey = input.readinessResult.repairableParameterNames.join('|');
  const readinessStateKey = [
    input.readinessResult.status,
    input.readinessResult.blockReason,
    input.catalogReady,
    input.catalogLoading,
    input.projectCatalog.parameterNames.length,
    input.sampledCatalog.names.length,
    input.familySatisfaction.satisfiedCount,
    missingParameterKey,
    blockedParameterKey,
    repairableParameterKey,
    input.repair.hasAttemptedRepair,
    input.repair.isRepairing,
    input.readinessResult.message,
  ].join('::');
  const lastLoggedReadinessStateKeyRef = useRef('');
  const lastLoggedTraceStateKeyRef = useRef('');

  useEffect(() => {
    if (lastLoggedTraceStateKeyRef.current === traceStateKey) {
      return;
    }
    lastLoggedTraceStateKeyRef.current = traceStateKey;
    traceMappingFlow('mapping readiness state', {
      projectKey: input.projectKey,
      normalizedProjectKey: input.normalizedProjectKey,
      modelVersion: input.modelVersion,
      profileKey: input.selectedProfileKey,
      signatureKey: toTraceKey(input.signature),
      status: input.readinessResult.status,
      message: input.readinessResult.message,
      isBlocking: input.readinessResult.isBlocking,
      blockReason: input.readinessResult.blockReason,
      referenceCount: input.referenceCount,
      catalogReady: input.catalogReady,
      catalogLoading: input.catalogLoading,
      waitingForSampledCatalog: input.waitingForSampledCatalog,
      catalogUnavailable: input.catalogUnavailable,
      runtimeAvailable: input.runtimeAvailable,
      hostRuntimeAvailable: input.hostRuntimeAvailable,
      projectCatalogLoaded: input.projectCatalog.isLoaded,
      projectCatalogMatchesProject: input.projectCatalog.matchesProject,
      projectCatalogError: input.projectCatalog.error,
      projectCatalogNameCount: input.projectCatalog.parameterNames.length,
      sampledCatalogReady: input.sampledCatalog.isReady,
      sampledCatalogLoading: input.sampledCatalog.isLoading,
      sampledCatalogNameCount: input.sampledCatalog.names.length,
      familySatisfactionApplies: input.familySatisfaction.applies,
      familySatisfactionReady: input.familySatisfaction.isReady,
      familySatisfiedCount: input.familySatisfaction.satisfiedCount,
      missingParameters: input.readinessResult.missingParameterNames,
      missingCount: input.readinessResult.missingParameterNames.length,
      blockedParameters: input.readinessResult.blockedParameterNames,
      blockedCount: input.readinessResult.blockedParameterNames.length,
      repairableParameters: input.readinessResult.repairableParameterNames,
      repairableCount: input.readinessResult.repairableParameterNames.length,
      repairKinds: input.repairKinds,
      hasAttemptedRepair: input.repair.hasAttemptedRepair,
      isRepairing: input.repair.isRepairing,
      repairErrorMessage: input.repair.repairErrorMessage,
    });
  }, [input, traceStateKey]);

  useEffect(() => {
    if (lastLoggedReadinessStateKeyRef.current === readinessStateKey) {
      return;
    }
    lastLoggedReadinessStateKeyRef.current = readinessStateKey;
    const missingCatalogPresence = input.readinessResult.missingParameterNames.map(
      (parameterName) => ({
        name: parameterName,
        inProjectCatalog: input.projectCatalog.parameterNames.some(
          (catalogName) => typeof catalogName === 'string' &&
            catalogName.trim().toLowerCase() === parameterName.trim().toLowerCase()
        ),
        inSampledCatalog: input.sampledCatalog.names.some(
          (catalogName) => typeof catalogName === 'string' &&
            catalogName.trim().toLowerCase() === parameterName.trim().toLowerCase()
        ),
      })
    );
    infoMappingFlow('mapping readiness state snapshot', {
      normalizedProjectKey: input.normalizedProjectKey,
      modelVersion: input.modelVersion,
      profileKey: input.selectedProfileKey,
      status: input.readinessResult.status,
      blockReason: input.readinessResult.blockReason,
      catalogReady: input.catalogReady,
      catalogLoading: input.catalogLoading,
      projectCatalogNameCount: input.projectCatalog.parameterNames.length,
      sampledCatalogReady: input.sampledCatalog.isReady,
      sampledCatalogNameCount: input.sampledCatalog.names.length,
      familySatisfiedCount: input.familySatisfaction.satisfiedCount,
      missingParameters: input.readinessResult.missingParameterNames,
      blockedParameters: input.readinessResult.blockedParameterNames,
      repairableParameters: input.readinessResult.repairableParameterNames,
      missingCatalogPresence,
      hasAttemptedRepair: input.repair.hasAttemptedRepair,
      isRepairing: input.repair.isRepairing,
      repairErrorMessage: input.repair.repairErrorMessage,
      message: input.readinessResult.message,
    });
  }, [input, readinessStateKey]);
}
